/**
 * @file Functions related to diagram switching: saving old and importing new diagrams, updating from master model
 */

import { Core } from 'cytoscape';
import { cy } from '../components/DiagramCanvas';
import {
  currentDiagram,
  propagation,
  PropagationEnum,
} from '../components/App';
import { DiagramTreeNode } from '../model/diagram-tree-model';
import { MMEdge } from '../model/edge-model';
import { ChildGroupType, MMNode } from '../model/node-model';
import {
  addConnectedNodes,
  clampCompoundChildrenAwayFromTitle,
  computeDisplayLabel,
  relayoutStateValueChildren,
} from './general';
import { restoreNodeSizeFromModel } from './node-interaction';
import { applyAutoLabelWidth } from '../utils/node-label-size';
import { cyStylesheet } from '../options/cytoscape-stylesheet';
import { smartFitView } from './general';
import { recalculateNamespacesForMasterModel } from '../utils/node-namespace';

const OPMCV_PERF_PREFIX = '[OPMCV-PERF]';
const opmcvNow = () =>
  typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
const opmcvPerfLog = (...args: any[]) =>
  console.log(OPMCV_PERF_PREFIX, ...args);
let updateFromMMSeq = 0;
let diagramSwitchingDepth = 0;
const initialSubdiagramViewResetSet = new WeakSet<DiagramTreeNode>();

export const isDiagramSwitchingInProgress = () => diagramSwitchingDepth > 0;

export const runWithDiagramSwitching = <T,>(callback: () => T): T => {
  diagramSwitchingDepth += 1;
  try {
    return callback();
  } finally {
    diagramSwitchingDepth -= 1;
  }
};

/**
 * Updates edge label on diagram switch. If the edge is merged (derived with multiple originals), name gets concatenated
 * @param MMRef - Master model edge
 * @returns - Single string or a concatenated name
 */
const getEdgeLabel = (MMRef: MMEdge): string => {
  const origEdges = MMRef.originalEdges;
  if (origEdges.length) {
    MMRef.label = origEdges[0].label;
    for (let i = 1; i < origEdges.length; i++) {
      const label = origEdges[i].label;
      if (label.length > 0) {
        MMRef.label += ', ' + label;
      }
    }
  }
  return MMRef.label;
};

const getHasSubdiagramFlag = (MMRef: MMNode | null | undefined): 0 | 1 =>
  MMRef?.diagram ? 1 : 0;

export const syncSubdiagramVisualStateFromMM = (cy: Core) => {
  cy.batch(() => {
    for (const node of cy.nodes() as any) {
      const MMRef = node.data('MMRef') as MMNode | null;
      if (!MMRef) continue;
      node.data({
        hasSubdiagram: getHasSubdiagramFlag(MMRef),
      });
    }
  });
};

/**
 * Delete elements marked as deleted or edges that were relinked, update labels
 * @param cy - Cytoscape instance
 */
const updateElementsFromMM = (cy: Core) => {
  recalculateNamespacesForMasterModel();
  const structureParentIds = new Set<string>();
  const inzoomParentIds = new Set<string>();
  // 批量更新/删除，减少每个节点/边操作触发的重算与渲染，降低主线程卡顿
  cy.batch(() => {
    for (const node of cy.nodes() as any) {
      const MMRef = node.data('MMRef') as MMNode;
      // 跳过没有 MMRef 的节点（如复制粘贴的节点可能暂时没有完整的 MMRef）
      if (!MMRef) continue;
      if (MMRef.type === 'state') {
        const parent = MMRef.parent as MMNode;
        if (parent?.deleted === true) {
          node.remove();
          continue;
        }
      }
      if (MMRef.deleted) {
        node.remove();
        continue;
      }

      // 注意：cy.json() 恢复节点时只会把 MMRef 补回去，node.data 可能缺少 childGroupType/displayLabel
      // 这里根据主模型同步一份到 cytoscape 数据，用于右键菜单显隐与标签渲染
      // 始终以 MMRef.label 为准，确保子图中修改名称后返回父图时能正确同步
      const childGroupType = MMRef.childGroupType || ChildGroupType.None;
      const rawLabel = MMRef.label || node.data('label');
      node.data({
        label: rawLabel,
        childGroupType,
        displayLabel: computeDisplayLabel(rawLabel, MMRef),
        namespace: MMRef.namespace || '',
        packageName: MMRef.packageName || '',
        hasSubdiagram: getHasSubdiagramFlag(MMRef),
      });

      // 从主模型恢复持久化尺寸，避免保存后重新进入时父节点尺寸丢失。
      restoreNodeSizeFromModel(node);
      if (
        node.isParent() &&
        (childGroupType === ChildGroupType.State ||
          childGroupType === ChildGroupType.Value)
      ) {
        structureParentIds.add(node.id());
      } else if (node.isParent() && childGroupType === ChildGroupType.InZoom) {
        inzoomParentIds.add(node.id());
      }

      applyAutoLabelWidth(node, MMRef.label);
    }

    for (const edge of cy.edges() as any) {
      const MMRef = edge.data('MMRef');
      // 跳过没有 MMRef 的边（如复制粘贴的边可能没有 MMRef）
      if (!MMRef) continue;
      if (
        MMRef.deleted ||
        edge.data('source') != MMRef.source?.id ||
        edge.data('target') != MMRef.target?.id ||
        MMRef.originalEdge?.deleted
      ) {
        edge.remove();
      } else {
        edge.data({
          label: getEdgeLabel(MMRef),
        });
      }
    }
  });

  // 状态/值父节点在导入或切图后需要重新布局子节点，确保标题保留区不会被遮挡。
  structureParentIds.forEach(nodeId => {
    const structureParent = cy.getElementById(nodeId);
    if (!structureParent || structureParent.empty()) return;
    relayoutStateValueChildren(structureParent);
  });

  inzoomParentIds.forEach(nodeId => {
    const inzoomParent = cy.getElementById(nodeId);
    if (!inzoomParent || inzoomParent.empty()) return;
    clampCompoundChildrenAwayFromTitle(inzoomParent);
  });
};

/**
 * 检查当前视口内是否有可见元素
 * @param cy - Cytoscape instance
 * @returns true 如果视口内有元素，false 如果视口内没有元素
 */
const hasElementsInViewport = (cy: Core): boolean => {
  const elements = cy.elements().filter(ele => ele.data('display') !== 'none');
  if (elements.length === 0) return false;

  // 获取当前视口边界
  const extent = cy.extent();
  const viewportBounds = {
    x1: extent.x1,
    y1: extent.y1,
    x2: extent.x2,
    y2: extent.y2,
  };

  // 检查是否有任何节点在视口内
  for (const node of cy.nodes() as any) {
    if (node.data('display') === 'none') continue;
    if (node.data('isMarkerLabel')) continue;
    const pos = node.position();
    if (
      pos.x >= viewportBounds.x1 &&
      pos.x <= viewportBounds.x2 &&
      pos.y >= viewportBounds.y1 &&
      pos.y <= viewportBounds.y2
    ) {
      return true;
    }
  }

  return false;
};

/**
 * 自动调整视图以显示所有元素
 * @param cy - Cytoscape instance
 */
const autoFitView = (cy: Core): void => {
  smartFitView(cy, 80);
};

export const resetSubdiagramViewOnInitialOpen = (
  diagram: DiagramTreeNode
): boolean => {
  if (
    !(diagram.mainNode instanceof MMNode) ||
    initialSubdiagramViewResetSet.has(diagram)
  ) {
    return false;
  }

  initialSubdiagramViewResetSet.add(diagram);
  setTimeout(() => autoFitView(cy), 50);
  return true;
};

/**
 * Main diagram switching logic. Save old diagram and display new one.
 * @param currentDiagram
 * @param nextDiagram
 */
export const switchDiagrams = (
  currentDiagram: DiagramTreeNode,
  nextDiagram: DiagramTreeNode
) => {
  runWithDiagramSwitching(() => {
    const json = cy.json();
    delete json.style; //cy instance has stylesheet, no need to save it with every diagram
    currentDiagram.diagramJson = json;
    cy.elements().remove();
    cy.json(nextDiagram.diagramJson);
    // 重新应用样式表，确保选中状态等样式正常工作
    cy.style().fromJson(cyStylesheet).update();
    // 从 master model 同步最新数据（包括名称），确保子图中修改的名称能正确反映到父图
    updateElementsFromMM(cy);
  });

  if (resetSubdiagramViewOnInitialOpen(nextDiagram)) {
    return;
  }

  // 切换后检查视口内是否有可见元素，如果没有则自动重置视图
  setTimeout(() => {
    if (!hasElementsInViewport(cy)) {
      console.log('视口内无可见元素，自动重置视图');
      autoFitView(cy);
    }
  }, 50); // 延迟执行，确保图表已完全渲染
};

/**
 * Go through all displayed nodes and propagate possible edges
 * 只有当全局 propagation 设置不为 None 时才传播边
 * @param cy Cytoscape instance
 */
const propagateFromMM = (cy: Core) => {
  // 当 propagation 设置为 None 时，不传播连接的边
  if (propagation === PropagationEnum.None) {
    return;
  }

  // 子图场景下不做动态补边：
  // 连线只在展开子图初始化时带入，后续由当前图层内手动连线维护，避免跨图层连线级联。
  if (currentDiagram.mainNode instanceof MMNode) {
    return;
  }

  for (const node of cy.nodes() as any) {
    if (node.data('isMarkerLabel')) continue;
    const MMRef = node.data('MMRef');
    if (!MMRef) continue;
    addConnectedNodes(cy, MMRef);
  }
};

/**
 * Update current diagram from master model
 */
export const updateFromMasterModel = () => {
  const seq = (updateFromMMSeq += 1);
  const totalStart = opmcvNow();
  opmcvPerfLog(`#UMM-${seq}`, 'updateFromMasterModel:start', {
    nodes: cy.nodes().length,
    edges: cy.edges().length,
  });

  const updateStart = opmcvNow();
  updateElementsFromMM(cy);
  opmcvPerfLog(
    `#UMM-${seq}`,
    'phase:updateElementsFromMM',
    `${(opmcvNow() - updateStart).toFixed(1)}ms`
  );

  const propagateStart = opmcvNow();
  propagateFromMM(cy);
  opmcvPerfLog(
    `#UMM-${seq}`,
    'phase:propagateFromMM',
    `${(opmcvNow() - propagateStart).toFixed(1)}ms`
  );

  opmcvPerfLog(
    `#UMM-${seq}`,
    'updateFromMasterModel:done',
    `${(opmcvNow() - totalStart).toFixed(1)}ms`
  );
};
